/**
 * KanbanToolbar.tsx - Toolbar with filters, search, and view controls
 * 
 * CHANGE HISTORY:
 * - 251108_0842 [current] danielbeer: feat: Create KanbanToolbar with filters and search
 */

import React, { useState } from 'react';
import { KanbanConfig, KanbanFilter, KanbanCardTemplate, BUILT_IN_FILTERS } from './KanbanConfig';

interface KanbanToolbarProps {
  config: KanbanConfig;
  onFilterChange?: (filters: KanbanFilter[]) => void;
  onSearchChange?: (query: string) => void;
  onViewModeChange?: (mode: 'default' | 'compact' | 'detailed') => void;
  onTemplateSelect?: (template: KanbanCardTemplate) => void;
  onAddCard?: () => void;
  selectedCount?: number;
  onBulkAction?: (action: string) => void;
}

export const KanbanToolbar: React.FC<KanbanToolbarProps> = ({
  config,
  onFilterChange,
  onSearchChange,
  onViewModeChange,
  onTemplateSelect,
  onAddCard,
  selectedCount = 0,
  onBulkAction,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearchChange?.(query);
  };

  const handleFilterToggle = (filterId: string) => {
    const newFilters = activeFilters.includes(filterId)
      ? activeFilters.filter(id => id !== filterId)
      : [...activeFilters, filterId];
    
    setActiveFilters(newFilters);
    
    // Convert filter IDs to filter objects
    const filterObjects = newFilters
      .map(id => BUILT_IN_FILTERS[id])
      .filter(Boolean);
    
    onFilterChange?.(filterObjects);
  };

  const handleClearFilters = () => {
    setActiveFilters([]);
    setSearchQuery('');
    onFilterChange?.([]);
    onSearchChange?.('');
  };

  const quickFilters = config.quickFilters || ['my-cards', 'overdue', 'high-priority'];

  return (
    <div className="kanban-toolbar" style={{
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '1rem',
      borderBottom: '1px solid var(--border-color, #e5e7eb)',
      backgroundColor: 'var(--bg-secondary, #f9fafb)',
      flexWrap: 'wrap',
    }}>
      {/* Search */}
      {config.enableSearch && (
        <div style={{ flex: '1 1 300px', minWidth: '200px' }}>
          <input
            type="text"
            placeholder="Search cards..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="kanban-search"
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: '6px',
              fontSize: '0.875rem',
            }}
          />
        </div>
      )}

      {/* Quick Filters */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {quickFilters.map(filterId => {
          const filter = BUILT_IN_FILTERS[filterId];
          if (!filter) return null;
          
          const isActive = activeFilters.includes(filterId);
          
          return (
            <button
              key={filterId}
              onClick={() => handleFilterToggle(filterId)}
              className="kanban-filter-btn"
              style={{
                padding: '0.375rem 0.75rem',
                border: `1px solid ${isActive ? filter.color : 'var(--border-color, #d1d5db)'}`,
                borderRadius: '6px',
                backgroundColor: isActive ? filter.color : 'transparent',
                color: isActive ? '#ffffff' : 'var(--text-primary, #1f2937)',
                fontSize: '0.875rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                transition: 'all 0.2s',
              }}
            >
              <span>{filter.icon}</span>
              <span>{filter.name}</span>
            </button>
          );
        })}
        
        {(activeFilters.length > 0 || searchQuery) && (
          <button
            onClick={handleClearFilters}
            className="kanban-clear-btn"
            style={{
              padding: '0.375rem 0.75rem',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              color: 'var(--text-secondary, #6b7280)',
              fontSize: '0.875rem',
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        )}
      </div>

      {/* View Mode Toggle */}
      <div style={{ display: 'flex', gap: '0.25rem', marginLeft: 'auto' }}>
        {(['default', 'compact', 'detailed'] as const).map(mode => (
          <button
            key={mode}
            onClick={() => onViewModeChange?.(mode)}
            className="kanban-view-btn"
            style={{
              padding: '0.375rem 0.75rem',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: '6px',
              backgroundColor: config.displayMode === mode ? 'var(--bg-primary, #ffffff)' : 'transparent',
              color: 'var(--text-primary, #1f2937)',
              fontSize: '0.875rem',
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {mode}
          </button>
        ))}
      </div>

      {/* Add Card with Templates */}
      {config.allowCardAdd && (
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => {
              if (config.cardTemplates && config.cardTemplates.length > 0) {
                setShowTemplates(!showTemplates);
              } else {
                onAddCard?.();
              }
            }}
            className="kanban-add-btn btn-primary"
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span>➕</span>
            <span>Add Card</span>
          </button>
          
          {showTemplates && config.cardTemplates && config.cardTemplates.length > 0 && (
            <div
              className="kanban-template-menu"
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '0.5rem',
                backgroundColor: 'var(--bg-primary, #ffffff)',
                border: '1px solid var(--border-color, #e5e7eb)',
                borderRadius: '6px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                minWidth: '200px',
                zIndex: 10,
              }}
            >
              {config.cardTemplates.map(template => (
                <button
                  key={template.id}
                  onClick={() => {
                    onTemplateSelect?.(template);
                    setShowTemplates(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    backgroundColor: 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-hover, #f3f4f6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span style={{ fontSize: '1.25rem' }}>{template.icon}</span>
                  <div>
                    <div style={{ fontWeight: 500 }}>{template.name}</div>
                    {template.description && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #6b7280)' }}>
                        {template.description}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bulk Actions */}
      {config.enableBulkOperations && selectedCount > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 1rem',
          backgroundColor: 'var(--bg-info, #dbeafe)',
          borderRadius: '6px',
        }}>
          <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>
            {selectedCount} selected
          </span>
          <button
            onClick={() => onBulkAction?.('move')}
            style={{
              padding: '0.25rem 0.5rem',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: '4px',
              backgroundColor: 'var(--bg-primary, #ffffff)',
              fontSize: '0.75rem',
              cursor: 'pointer',
            }}
          >
            Move
          </button>
          <button
            onClick={() => onBulkAction?.('delete')}
            style={{
              padding: '0.25rem 0.5rem',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: '4px',
              backgroundColor: 'var(--bg-primary, #ffffff)',
              fontSize: '0.75rem',
              cursor: 'pointer',
              color: 'var(--color-danger, #ef4444)',
            }}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};
